import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, ImagePlus, Trash2, Package, DollarSign } from 'lucide-react';
import {
  Button,
  Input,
  Label,
  Textarea,
  Card,
  Select,
  Checkbox,
  Alert,
  Spinner,
  apiClient,
  useAuthStore,
  useNotificationStore,
} from '@dukene/shared';
import { confirmAction } from '../../components/modals/ConfirmModal';
import { ProductStatus, MERCHANT_ROUTES } from '@dukene/shared';
import VariantManager, { Attribute, Variant } from '../../components/products/VariantManager';

// Backend product interface
interface BackendProduct {
  id: string;
  storeId: string;
  title: string;
  description: string | null;
  handle: string;
  price: string;
  compareAtPrice: string | null;
  costPerItem: string | null;
  sku: string | null;
  barcode: string | null;
  trackInventory: boolean;
  status: ProductStatus;
  tags: string[];
  vendor: string | null;
  productType: string | null;
  weight: string | null;
  weightUnit: string | null;
  featured?: boolean;
  isNew?: boolean;
  onSale?: boolean;
  metaTitle?: string | null;
  metaDescription?: string | null;
  metaKeywords?: string | null;
  affiliateEnabled: boolean;
  commissionType: 'PERCENTAGE' | 'FIXED' | null;
  commissionValue: string | null;
  images: Array<{
    id: string;
    url: string;
    altText: string | null;
    position: number;
  }>;
  categories: Array<{
    id: string;
    name: string;
    slug: string;
  }>;
  variants: any[];
  inventory?: {
    available: number;
    trackQuantity: boolean;
    lowStockThreshold: number;
  };
}

// Validation Schema
const productSchema = z.object({
  title: z.string().min(3, 'Product name must be at least 3 characters'),
  description: z.string().min(10, 'Description must be at least 10 characters'),
  sku: z.string().min(2, 'SKU is required'),
  price: z.number().min(0, 'Price must be positive'),
  compareAtPrice: z.preprocess((val) => {
    if (val === '' || val === null || val === undefined || (typeof val === 'number' && isNaN(val))) return undefined;
    return Number(val);
  }, z.number().optional()),
  costPerItem: z.preprocess((val) => {
    if (val === '' || val === null || val === undefined || (typeof val === 'number' && isNaN(val))) return undefined;
    return Number(val);
  }, z.number().optional()),
  categoryId: z.string().optional(),
  status: z.nativeEnum(ProductStatus),
  tags: z.string(),
  vendor: z.string().optional(),
  productType: z.string().optional(),
  weight: z.preprocess((val) => {
    if (val === '' || val === null || val === undefined || (typeof val === 'number' && isNaN(val))) return undefined;
    return Number(val);
  }, z.number().optional().nullable()),
  weightUnit: z.string().optional(),
  trackInventory: z.boolean(),
  featured: z.boolean().optional(),
  isNew: z.boolean().optional(),
  onSale: z.boolean().optional(),
  initialQuantity: z.number().min(0, 'Quantity must be 0 or greater').optional(),
  lowStockThreshold: z.number().min(0, 'Threshold must be 0 or greater').optional(),
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
  metaKeywords: z.string().optional(),
  affiliateEnabled: z.boolean().optional().default(false),
  commissionType: z.enum(['PERCENTAGE', 'FIXED']).optional(),
  commissionValue: z.number().min(0).optional(),
});

type ProductFormData = z.infer<typeof productSchema>;

export default function ProductEdit() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const activeStoreId = useAuthStore((state) => state.activeStoreId);
  const [existingImages, setExistingImages] = useState<any[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState<number>(0);

  // Variant state using VariantManager types
  const [hasVariants, setHasVariants] = useState(false);
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [variants, setVariants] = useState<Variant[]>([]);

  // Fetch product
  const { data: product, isLoading, error } = useQuery<BackendProduct>({
    queryKey: ['product', activeStoreId, id],
    queryFn: async () => {
      if (!id) throw new Error('Product ID is required');
      return apiClient.get<BackendProduct>(`/products/${id}`);
    },
    enabled: !!id && !!activeStoreId,
  });

  // Fetch categories
  const { data: categories } = useQuery<Array<{ id: string; name: string; slug: string }>>({
    queryKey: ['categories', activeStoreId],
    queryFn: async () => {
      return apiClient.get('/products/categories');
    },
    enabled: !!activeStoreId,
  });

  // Form setup
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(productSchema),
    defaultValues: {
      title: '',
      description: '',
      sku: '',
      price: 0,
      status: ProductStatus.DRAFT,
      tags: '',
      trackInventory: true,
      categoryId: '',
      affiliateEnabled: false,
    },
  });

  const affiliateEnabledValue = watch('affiliateEnabled');
  const commissionTypeValue = watch('commissionType');
  const commissionValueValue = watch('commissionValue');
  const priceValue = watch('price');

  // Reset form when product data loads
  useEffect(() => {
    if (product) {
      reset({
        title: product.title,
        description: product.description || '',
        sku: product.sku || '',
        price: parseFloat(product.price),
        compareAtPrice: product.compareAtPrice ? parseFloat(product.compareAtPrice) : undefined,
        costPerItem: product.costPerItem ? parseFloat(product.costPerItem) : undefined,
        categoryId: product.categories?.[0]?.id || '',
        status: product.status,
        tags: product.tags?.join(', ') || '',
        vendor: product.vendor || '',
        productType: product.productType || '',
        weight: product.weight ? parseFloat(product.weight) : undefined,
        weightUnit: product.weightUnit || 'kg',
        trackInventory: product.trackInventory,
        featured: product.featured || false,
        isNew: product.isNew || false,
        onSale: product.onSale || false,
        initialQuantity: product.inventory?.available || 0,
        lowStockThreshold: product.inventory?.lowStockThreshold || 10,
        metaTitle: product.metaTitle || '',
        metaDescription: product.metaDescription || '',
        metaKeywords: product.metaKeywords || '',
        affiliateEnabled: product.affiliateEnabled || false,
        commissionType: (product.commissionType as any) || 'PERCENTAGE',
        commissionValue: product.commissionValue ? parseFloat(product.commissionValue) : undefined,
      });
      // Always update existingImages when product data changes
      setExistingImages(product.images || []);

      // Load variants for VariantManager
      if (product.variants && product.variants.length > 0) {
        // 1. Reconstruct attributes from backend product data
        const loadedAttributes: Attribute[] = (product as any).attributes?.map((attr: any) => ({
          name: attr.name,
          type: attr.type,
          values: attr.values.map((v: any) => v.value),
        })) || [];
        setAttributes(loadedAttributes);

        // 2. Transform variants to VariantManager format (Record<string, string> for attributeValues)
        const loadedVariants: Variant[] = product.variants.map((v: any) => {
          const attributeValues: Record<string, string> = {};
          v.attributeValues?.forEach((av: any) => {
            if (av.attributeValue?.attribute?.name) {
              attributeValues[av.attributeValue.attribute.name] = av.attributeValue.value;
            }
          });

          return {
            title: v.title,
            sku: v.sku || '',
            price: v.price,
            compareAtPrice: v.compareAtPrice ? parseFloat(String(v.compareAtPrice)) : undefined,
            quantity: v.inventoryQuantity,
            available: v.available !== false,
            image: v.image || undefined,
            attributeValues,
          };
        });

        // Determine if product has real variants (more than just a "Default Title" placeholder)
        const hasRealVariants = loadedVariants.length > 0 &&
          (loadedVariants.length > 1 || (loadedVariants[0].title !== 'Default Title' && loadedAttributes.length > 0));

        if (hasRealVariants) {
          setHasVariants(true);
          setVariants(loadedVariants);
        }
      }
    }
  }, [product, reset]);

  // Update product mutation
  const updateProduct = useMutation({
    mutationFn: async (data: ProductFormData) => {
      if (!id) throw new Error('Product ID is required');

      // Parse tags
      const tagsArray = data.tags.split(',').map(t => t.trim()).filter(Boolean);

      // Calculate initialQuantity based on whether product has variants
      const calculatedInitialQuantity = hasVariants && variants.length > 0
        ? variants.reduce((sum, v) => sum + (v.quantity || 0), 0)
        : data.initialQuantity;

      // Step 1: Upload variant images if they are in base64 format
      const variantsWithUploadedImages = await Promise.all(
        variants.map(async (variant) => {
          if (variant.image && variant.image.startsWith('data:')) {
            // Convert base64 data URL to blob for upload
            const fetchResponse = await fetch(variant.image);
            const blob = await fetchResponse.blob();

            const variantFormData = new FormData();
            variantFormData.append('file', blob, `variant-${variant.sku || 'v'}.jpg`);
            variantFormData.append('folder', 'variants');

            try {
              const uploadResponse = await apiClient.getClient().post('/uploads/image', variantFormData, {
                headers: { 
                  'Content-Type': 'multipart/form-data',
                  'X-Store-Id': activeStoreId || ''
                },
              });

              return {
                ...variant,
                image: uploadResponse.data.data.url,
              };
            } catch (error) {
              console.error('Failed to upload variant image:', error);
              return { ...variant, image: undefined };
            }
          }
          return variant;
        })
      );

      // Step 2: Prepare payload with formatted variants
      const payload = {
        categories: data.categoryId ? [data.categoryId] : [],
        title: data.title,
        description: data.description,
        sku: data.sku,
        price: data.price,
        compareAtPrice: data.compareAtPrice,
        costPerItem: data.costPerItem,
        status: data.status,
        tags: tagsArray,
        vendor: data.vendor,
        productType: data.productType,
        weight: data.weight,
        weightUnit: data.weightUnit,
        trackInventory: data.trackInventory,
        featured: data.featured,
        isNew: data.isNew,
        onSale: data.onSale,
        // Always send initialQuantity - either from form or calculated from variants
        initialQuantity: calculatedInitialQuantity,
        lowStockThreshold: data.lowStockThreshold,
        metaTitle: data.metaTitle,
        metaDescription: data.metaDescription,
        metaKeywords: data.metaKeywords,
        affiliateEnabled: data.affiliateEnabled,
        commissionType: data.affiliateEnabled ? data.commissionType : undefined,
        commissionValue: data.affiliateEnabled ? data.commissionValue : undefined,
        // Add attributes if variants exist
        ...(hasVariants && attributes.length > 0 && {
          attributes: attributes.map(attr => ({
            name: attr.name,
            type: attr.type,
            values: attr.values,
          })),
        }),
        // Add formatted variant data
        variants: variantsWithUploadedImages.map(v => ({
          title: v.title,
          price: v.price,
          sku: v.sku,
          quantity: v.quantity,
          // Send null explicitly so removing an existing variant image is
          // distinguishable from leaving the field unchanged.
          image: v.image ?? null,
          attributeValues: Object.entries(v.attributeValues).map(([attributeName, value]) => ({
            attributeName,
            value,
          })),
        })),
      };

      return apiClient.patch(`/products/${id}`, payload);
    },
    onSuccess: () => {
      const storeId = useAuthStore.getState().activeStoreId;
      queryClient.invalidateQueries({ queryKey: ['merchant-products', storeId] });
      queryClient.invalidateQueries({ queryKey: ['product', storeId, id] });

      useNotificationStore.getState().addNotification({
        type: 'success',
        title: 'Success',
        message: 'Product updated successfully',
      });

      navigate(MERCHANT_ROUTES.PRODUCTS);
    },
  });

  // Upload a single image to Cloudinary, then attach it to the product
  const uploadSingleImage = async (file: File, position: number): Promise<void> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'products');

    // getClient() returns the raw axios instance — interceptors still run (auth, X-Store-Id)
    const response = await apiClient.getClient().post('/uploads/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-Store-Id': activeStoreId || '',
      },
    });

    const uploadedUrl = response.data?.data?.url;
    if (!uploadedUrl) throw new Error('Upload succeeded but no URL returned');

    await apiClient.post(`/products/${id}/images`, {
      url: uploadedUrl,
      altText: product?.title || '',
      position,
    });
  };

  // Handle image file selection
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    setImageUploadProgress(0);

    const totalFiles = files.length;
    let successCount = 0;

    for (let i = 0; i < totalFiles; i++) {
      try {
        await uploadSingleImage(files[i], existingImages.length + i);
        successCount++;
        setImageUploadProgress(((i + 1) / totalFiles) * 100);
        // Refresh product images after each successful upload so the UI updates
        const storeId = useAuthStore.getState().activeStoreId;
        queryClient.invalidateQueries({ queryKey: ['product', storeId, id] });
      } catch (error) {
        console.error(`Image upload failed for file ${i + 1}:`, error);
        // Continue uploading remaining files even if one fails
      }
    }

    setUploadingImages(false);
    setImageUploadProgress(0);
    // Reset file input
    event.target.value = '';

    if (successCount < totalFiles) {
      console.warn(`${totalFiles - successCount} of ${totalFiles} images failed to upload`);
    }
  };


  // Delete image mutation
  const deleteImageMutation = useMutation({
    mutationFn: async (imageId: string) => {
      if (!id) throw new Error('Product ID is required');
      return apiClient.delete(`/products/${id}/images/${imageId}`);
    },
    onMutate: async (imageId: string) => {
      await queryClient.cancelQueries({ queryKey: ['product', activeStoreId, id] });
      const previousImages = existingImages;
      setExistingImages((images) => images.filter((image) => image.id !== imageId));
      return { previousImages };
    },
    onError: (_error, _imageId, context) => {
      if (context?.previousImages) setExistingImages(context.previousImages);
      useNotificationStore.getState().addNotification({
        type: 'error',
        title: 'Image not deleted',
        message: 'The image could not be deleted. Please try again.',
      });
    },
    onSuccess: () => {
      const storeId = useAuthStore.getState().activeStoreId;
      queryClient.invalidateQueries({ queryKey: ['product', storeId, id] });
      queryClient.invalidateQueries({ queryKey: ['merchant-products', storeId] });
      useNotificationStore.getState().addNotification({
        type: 'success',
        title: 'Image deleted',
        message: 'The product image was deleted successfully.',
      });
    },
  });

  // Handle image deletion

  const handleDeleteImage = async (imageId: string) => {
    const confirmed = await confirmAction({
      title: 'Delete Image',
      message: 'Are you sure you want to delete this image?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });
    if (confirmed) {
      deleteImageMutation.mutate(imageId);
    }
  };

  const onSubmit = (data: ProductFormData) => {
    updateProduct.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <Alert variant="destructive">
        <p>Failed to load product. Please try again.</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate(MERCHANT_ROUTES.PRODUCTS)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Products
        </Button>
      </Alert>
    );
  }


  return (
    <div className="max-w-5xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(MERCHANT_ROUTES.PRODUCTS)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Edit Product</h1>
            <p className="text-muted-foreground mt-1">{product.title}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-sm text-muted-foreground">SKU:</span>
              <code className="px-2 py-1 bg-secondary text-secondary-foreground rounded text-sm font-mono">
                {product.sku || '(No SKU)'}
              </code>
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {updateProduct.isError && (
        <Alert variant="destructive">
          {updateProduct.error?.message || 'Failed to update product'}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <Card className="p-6 space-y-4">
              <h2 className="text-xl font-semibold">Basic Information</h2>

              <div>
                <Label htmlFor="title">Product Name *</Label>
                <Input
                  id="title"
                  {...register('title')}
                  placeholder="e.g., Wireless Bluetooth Headphones"
                />
                {errors.title && (
                  <p className="text-sm text-destructive mt-1">{errors.title.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  {...register('description')}
                  rows={6}
                  placeholder="Detailed product description"
                />
                {errors.description && (
                  <p className="text-sm text-destructive mt-1">
                    {errors.description.message}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="vendor">Vendor</Label>
                  <Input
                    id="vendor"
                    {...register('vendor')}
                    placeholder="e.g., Nike, Apple"
                  />
                </div>

                <div>
                  <Label htmlFor="productType">Product Type</Label>
                  <Input
                    id="productType"
                    {...register('productType')}
                    placeholder="e.g., Electronics, Apparel"
                  />
                </div>
              </div>
            </Card>

            {/* Product Images */}
            <Card className="p-6 space-y-4">
              <h2 className="text-xl font-semibold">Product Images</h2>

              {/* Upload Button */}
              <div className="flex items-center gap-4">
                <label
                  htmlFor="image-upload"
                  className={`
                    inline-flex items-center gap-2 px-4 py-2 rounded
                    bg-primary text-primary-foreground font-medium cursor-pointer
                    hover:bg-primary/90 transition-colors
                    ${uploadingImages ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  <ImagePlus className="w-4 h-4" />
                  {uploadingImages ? 'Uploading...' : 'Upload Images'}
                </label>
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  disabled={uploadingImages}
                  className="hidden"
                />
                <span className="text-sm text-muted-foreground">
                  {uploadingImages
                    ? `Uploading... ${Math.round(imageUploadProgress)}%`
                    : 'Upload up to 10 images (max 5MB each)'}
                </span>
              </div>

              {/* Upload Progress Bar */}
              {uploadingImages && (
                <div className="w-full bg-secondary rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${imageUploadProgress}%` }}
                  />
                </div>
              )}

              {/* Existing Images Grid */}
              {existingImages.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {existingImages.map((image, index) => (
                    <div
                      key={image.id || `existing-${index}`}
                      className="relative aspect-square group"
                    >
                      <img
                        src={image.url}
                        alt={image.altText || `Product image ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg border border-border"
                      />

                      {/* Delete Button Overlay */}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteImage(image.id)}
                          disabled={deleteImageMutation.isPending}
                          className="rounded-full"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>

                      {/* Primary Badge */}
                      {index === 0 && (
                        <div className="absolute top-2 left-2 bg-primary text-primary-foreground text-xs px-2 py-1 rounded">
                          Primary
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border-2 border-dashed border-border rounded-lg p-12 text-center">
                  <ImagePlus className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-foreground mb-2">No images uploaded yet</p>
                  <p className="text-sm text-muted-foreground">
                    Click "Upload Images" to add product photos
                  </p>
                </div>
              )}

              <p className="text-sm text-muted-foreground">
                The first image will be used as the primary product image. Drag to reorder (coming soon).
              </p>
            </Card>

            {/* Pricing */}
            <Card className="p-6 space-y-4">
              <h2 className="text-xl font-semibold">Pricing</h2>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="price">selling Price (KES) *</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    {...register('price', { valueAsNumber: true })}
                  />
                  {errors.price && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.price.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="compareAtPrice">discounted price</Label>
                  <Input
                    id="compareAtPrice"
                    type="number"
                    step="0.01"
                    {...register('compareAtPrice', { valueAsNumber: true })}
                  />
                </div>

                <div>
                  <Label htmlFor="costPerItem">Buying price</Label>
                  <Input
                    id="costPerItem"
                    type="number"
                    step="0.01"
                    {...register('costPerItem', { valueAsNumber: true })}
                  />
                </div>
              </div>
            </Card>

            {/* Inventory & Shipping */}
            <Card className="p-6 space-y-4">
              <h2 className="text-xl font-semibold">Inventory & Shipping</h2>

              <div>
                <Label htmlFor="sku">SKU *</Label>
                <Input id="sku" {...register('sku')} />
                {errors.sku && (
                  <p className="text-sm text-destructive mt-1">{errors.sku.message}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Checkbox id="trackInventory" {...register('trackInventory')} />
                <Label htmlFor="trackInventory" className="cursor-pointer">
                  Track inventory quantity
                </Label>
              </div>

              {/* Current Inventory Status */}
              {product.inventory && (
                <div className={`p-4 rounded-lg border ${product.inventory.available <= product.inventory.lowStockThreshold
                  ? 'bg-yellow-50 border-yellow-200'
                  : product.inventory.available === 0
                    ? 'bg-red-50 border-red-200'
                    : 'bg-green-50 border-green-200'
                  }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground">Current Stock</p>
                      <p className="text-2xl font-bold">
                        {product.inventory.available} units
                      </p>
                    </div>
                    {product.inventory.available <= product.inventory.lowStockThreshold && product.inventory.available > 0 && (
                      <span className="text-yellow-600 text-sm font-medium">⚠️ Low Stock</span>
                    )}
                    {product.inventory.available === 0 && (
                      <span className="text-red-600 text-sm font-medium">❌ Out of Stock</span>
                    )}
                  </div>
                </div>
              )}

              {/* Quantity Management - Only for non-variant products */}
              {!hasVariants && (
                <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                  <h3 className="text-sm font-medium">Inventory Management</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="initialQuantity">Stock Quantity *</Label>
                      <Input
                        id="initialQuantity"
                        type="number"
                        min="0"
                        {...register('initialQuantity', { valueAsNumber: true })}
                      />
                      {errors.initialQuantity && (
                        <p className="text-sm text-destructive mt-1">
                          {errors.initialQuantity.message}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        Available units in stock
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="lowStockThreshold">Low Stock Alert</Label>
                      <Input
                        id="lowStockThreshold"
                        type="number"
                        min="0"
                        {...register('lowStockThreshold', { valueAsNumber: true })}
                      />
                      {errors.lowStockThreshold && (
                        <p className="text-sm text-destructive mt-1">
                          {errors.lowStockThreshold.message}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        Alert when stock falls below this number
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Info for variant products */}
              {hasVariants && variants.length > 0 && (
                <Alert>
                  <p className="text-sm flex items-start gap-2">
                    <Package className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>This product has variants. Inventory is managed per variant in the Variants section below.</span>
                  </p>
                </Alert>
              )}

              {/* Product Features */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium">Product Features</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox id="featured" {...register('featured')} />
                    <Label htmlFor="featured" className="cursor-pointer">
                      <div className="flex flex-col">
                        <span>Featured Product</span>
                        <span className="text-xs text-muted-foreground font-normal">
                          Display prominently on homepage
                        </span>
                      </div>
                    </Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox id="isNew" {...register('isNew')} />
                    <Label htmlFor="isNew" className="cursor-pointer">
                      <div className="flex flex-col">
                        <span>New Arrival</span>
                        <span className="text-xs text-muted-foreground font-normal">
                          Mark as new product
                        </span>
                      </div>
                    </Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox id="onSale" {...register('onSale')} />
                    <Label htmlFor="onSale" className="cursor-pointer">
                      <div className="flex flex-col">
                        <span>On Sale</span>
                        <span className="text-xs text-muted-foreground font-normal">
                          Show sale badge
                        </span>
                      </div>
                    </Label>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="weight">Weight</Label>
                  <Input
                    id="weight"
                    type="number"
                    step="0.01"
                    {...register('weight', { valueAsNumber: true })}
                  />
                </div>

                <div>
                  <Label htmlFor="weightUnit">Weight Unit</Label>
                  <Select {...register('weightUnit')}>
                    <option value="kg">Kilograms (kg)</option>
                    <option value="g">Grams (g)</option>
                    <option value="lb">Pounds (lb)</option>
                    <option value="oz">Ounces (oz)</option>
                  </Select>
                </div>
              </div>
            </Card>

            <VariantManager
              hasVariants={hasVariants}
              onHasVariantsChange={setHasVariants}
              attributes={attributes}
              onAttributesChange={setAttributes}
              variants={variants}
              onVariantsChange={setVariants}
              defaultPrice={priceValue}
            />

            {/* SEO Settings */}
            <Card className="p-6 space-y-4">
              <h2 className="text-xl font-semibold">SEO Settings</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="metaTitle">Meta Title</Label>
                  <Input
                    id="metaTitle"
                    {...register('metaTitle')}
                    placeholder="SEO Title"
                  />
                </div>
                <div>
                  <Label htmlFor="metaDescription">Meta Description</Label>
                  <Textarea
                    id="metaDescription"
                    {...register('metaDescription')}
                    rows={3}
                    placeholder="SEO Description"
                  />
                </div>
                <div>
                  <Label htmlFor="metaKeywords">Meta Keywords</Label>
                  <Input
                    id="metaKeywords"
                    {...register('metaKeywords')}
                    placeholder="Keywords (comma separated)"
                  />
                </div>
              </div>
            </Card>

            {/* Affiliate Marketing */}
            <Card className="p-6 space-y-4">
              <h2 className="text-xl font-semibold">Affiliate Marketing</h2>

              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div className="flex-1">
                  <Label htmlFor="affiliateEnabled" className="text-base font-medium cursor-pointer">
                    Enable Affiliate Marketing
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Allow affiliates to promote this product
                  </p>
                </div>
                <Checkbox
                  id="affiliateEnabled"
                  {...register('affiliateEnabled')}
                />
              </div>

              {affiliateEnabledValue && (
                <div className="space-y-4 pt-4 border-t">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="commissionType">Commission Type</Label>
                      <Select id="commissionType" {...register('commissionType')}>
                        <option value="PERCENTAGE">Percentage (%)</option>
                        <option value="FIXED">Fixed Amount (KES)</option>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="commissionValue">
                        Value ({commissionTypeValue === 'PERCENTAGE' ? '%' : 'KES'})
                      </Label>
                      <Input
                        id="commissionValue"
                        type="number"
                        step="0.01"
                        {...register('commissionValue', { valueAsNumber: true })}
                      />
                    </div>
                  </div>

                  {/* Earnings Preview */}
                  {priceValue > 0 && commissionValueValue && commissionValueValue > 0 && (
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/10 dark:to-blue-900/10 p-4 rounded-lg border border-purple-100 dark:border-purple-800">
                      <p className="text-sm font-semibold text-purple-900 dark:text-purple-100 mb-2 flex items-center gap-2">
                        <DollarSign className="w-4 h-4" />
                        Earnings Preview
                      </p>
                      <div className="space-y-1">
                        <p className="text-xs text-purple-700 dark:text-purple-300">
                          Affiliate Earns:{' '}
                          <span className="font-semibold">
                            KES{' '}
                            {commissionTypeValue === 'PERCENTAGE'
                              ? ((priceValue * commissionValueValue) / 100).toFixed(2)
                              : commissionValueValue.toFixed(2)}
                          </span>
                        </p>
                        <p className="text-xs text-purple-700 dark:text-purple-300">
                          Your Net:{' '}
                          <span className="font-semibold">
                            KES{' '}
                            {commissionTypeValue === 'PERCENTAGE'
                              ? (priceValue - (priceValue * commissionValueValue) / 100).toFixed(2)
                              : (priceValue - commissionValueValue).toFixed(2)}
                          </span>
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status */}
            <Card className="p-6 space-y-4">
              <h2 className="text-xl font-semibold">Status</h2>

              <div>
                <Label htmlFor="status">Product Status</Label>
                <Select id="status" {...register('status')}>
                  <option value="ACTIVE">Active</option>
                  <option value="DRAFT">Draft</option>
                  <option value="ARCHIVED">Archived</option>
                </Select>
              </div>
            </Card>

            {/* Organization */}
            <Card className="p-6 space-y-4">
              <h2 className="text-xl font-semibold">Organization</h2>

              <div>
                <Label htmlFor="category">Category</Label>
                <Select id="category" {...register('categoryId')}>
                  <option value="">No category</option>
                  {categories?.map((category: any) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <Label htmlFor="vendor">Vendor</Label>
                <Input id="vendor" {...register('vendor')} />
              </div>

              <div>
                <Label htmlFor="productType">Product Type</Label>
                <Input id="productType" {...register('productType')} placeholder="e.g., Electronics, Clothing" />
              </div>

              <div>
                <Label htmlFor="tags">Tags (comma-separated)</Label>
                <Input id="tags" {...register('tags')} placeholder="e.g., sale, featured, new" />
              </div>
            </Card>

            {/* Actions */}
            <Card className="p-6 space-y-4">
              <Button
                type="submit"
                className="w-full"
                disabled={updateProduct.isPending}
              >
                {updateProduct.isPending ? (
                  'Saving...'
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => navigate(MERCHANT_ROUTES.PRODUCTS)}
              >
                Cancel
              </Button>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
}
